import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { StudentProfileForm } from '../../../../constants/types/student-profile-form';
import FormRow from '../../../common/input/FormRow';
import TextWinds from '../../../common/TextWinds';
import {
  birthdayRules,
  loosePhoneRules,
  nameRules,
} from '../../../../constants/validation';
import { Field } from '../../../common/form/Field';
import SectionHeader, {
  SectionActionProps,
} from '../../../common/form/SectionHeader';
import HFbutton from '../../../button/HFbutton';

/** 보호자 최대 등록 가능 수 (학생 정보 수정 API는 부모 여러 명 등록을 지원하지만, 학생 화면과 정책을 맞추기 위해 화면에서 1명으로 제한합니다.) */
const MAX_PARENTS = 1;

/**
 * LMS > 학생 관리 > 학생 상세 정보 > 보호자 정보 영역.
 * @description
 * - 선생님이 학생의 보호자 목록을 대신 수정합니다.
 * - 보호자 1명의 정보를 통째로 추가/삭제할 수 있고, 저장 시 배열이 통째로 교체됩니다. 한번 등록할때 보호자의 모든 속성을 입력해야 합니다.
 * - INFO: 학생 정보 수정 API는 부모 여러 명 등록을 지원하지만(parents:[]), 학생 화면과 정책을 맞추기 위해 화면에서 1명으로 제한합니다.
 *   (서버가 개수를 강제하지는 않으므로 화면에서 막는다)
 */
export const ParentsInfoSection = ({
  control,
  errors,
  section,
  fields,
  append,
  remove,
}: {
  control: Control<StudentProfileForm>;
  errors: FieldErrors<StudentProfileForm>;
  section: SectionActionProps;
  /** useFieldArray는 StudentDetail에서 관리한다. (취소 시 reset과 함께 다뤄야 하므로) */
  fields: FieldArrayWithId<StudentProfileForm, 'parents', 'id'>[];
  append: UseFieldArrayAppend<StudentProfileForm, 'parents'>;
  remove: UseFieldArrayRemove;
}) => {
  const isEditing = section.isEditing;

  return (
    <section className="mt-6">
      <SectionHeader title="보호자 정보" {...section} />
      <div className="mt-5 flex flex-col gap-y-2.5">
        {fields.length === 0 ? (
          <TextWinds type="content_body2" className="m-auto mb-4 text-gray6">
            등록된 보호자가 없습니다
          </TextWinds>
        ) : (
          fields.map((item, index) => {
            const parentErrors = errors.parents?.[index];

            return (
              <div key={item.id} className="rounded bg-gray0 p-4">
                <div className="flex justify-end">
                  {isEditing && (
                    <HFbutton
                      type="red-dashed"
                      width="auto"
                      height={36}
                      onClick={() => remove(index)}
                    >
                      <span className="flex items-center justify-center gap-2 text-sm">
                        <TrashIcon className="size-5" />
                        삭제하기
                      </span>
                    </HFbutton>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <Field
                    label="보호자 이름"
                    error={parentErrors?.name?.message}
                  >
                    <FormRow
                      type="text"
                      name={`parents.${index}.name`}
                      control={control}
                      placeholder="보호자 이름 입력"
                      rules={nameRules({
                        label: '보호자 이름',
                        required: true,
                      })}
                      error={!!parentErrors?.name}
                      disabled={!isEditing}
                      autoComplete="off"
                    />
                  </Field>

                  <Field
                    label="보호자 전화번호"
                    error={parentErrors?.phone?.message}
                  >
                    <FormRow
                      type="phone"
                      name={`parents.${index}.phone`}
                      control={control}
                      placeholder="- 없이 번호만 입력"
                      rules={loosePhoneRules({
                        label: '보호자 전화번호',
                        required: true,
                      })}
                      error={!!parentErrors?.phone}
                      disabled={!isEditing}
                      autoComplete="off"
                    />
                  </Field>

                  <Field
                    label="보호자 생년월일"
                    error={parentErrors?.birthday?.message}
                  >
                    <FormRow
                      type="birthday"
                      name={`parents.${index}.birthday`}
                      control={control}
                      rules={birthdayRules({
                        label: '보호자 생년월일',
                        required: true,
                      })}
                      disabled={!isEditing}
                      error={!!parentErrors?.birthday}
                    />
                  </Field>

                  <Field
                    label="보호자 성별"
                    error={parentErrors?.parentType?.message}
                  >
                    <FormRow
                      type="gender"
                      name={`parents.${index}.parentType`}
                      control={control}
                      rules={{
                        required: '보호자 성별을 선택해주세요',
                      }}
                      error={!!parentErrors?.parentType}
                      disabled={!isEditing}
                    />
                  </Field>
                </div>
              </div>
            );
          })
        )}

        {isEditing && fields.length < MAX_PARENTS && (
          <HFbutton
            type="primary-dashed"
            onClick={() =>
              append({
                name: '',
                phone: '',
                parentType: '',
                birthday: '',
              })
            }
          >
            <span className="flex items-center justify-center gap-2 text-sm">
              <PlusCircleIcon className="size-5" />
              보호자 추가하기
            </span>
          </HFbutton>
        )}
      </div>
    </section>
  );
};
